import { useDispatch, useSelector } from 'react-redux';

import { selectShouldCheckIfConsentResetRequired } from 'app/store/data/consents';
import { WrappedControlProps } from 'app/components/Controls/WrappedControlProps';
import { getCapitalized } from 'app/shared/helpers/getCapitalized';
import { clearAnonymization } from 'app/shared/helpers/clearAnonymization';
import { getQuotationId, selectQuotation } from 'app/store/data/quotation';
import { FieldsEnum, FormsEnum } from 'app/shared/enums';
import {
  checkAndResetMarketingConsentIfRequired,
  getConsentResetPersonFlag,
  getConsentResetPersonGroup,
  shouldCheckConsentResetForTrackedPerson,
} from 'app/shared/helpers/checkAndResetMarketingConsent';

function containsValidCharacters(value: string): boolean {
  return /^$|^[a-zA-ZąĄćĆęĘłŁńŃóÓśŚżŻźŹ]+(|[*]{3}|[*]{2}|[*])$/.test(value);
}

export function useNameControl({
  input,
  onValueChange,
  questionKey,
  formName = FormsEnum.CalculationForm,
}: WrappedControlProps) {
  const dispatch = useDispatch();
  const shouldCheckIfConsentResetRequired = useSelector(
    selectShouldCheckIfConsentResetRequired
  );
  const quotationId = useSelector(getQuotationId);
  const { insurantSelection } = useSelector(selectQuotation);

  const handleChange = (value: string) => {
    if (!containsValidCharacters(value) || value.length > 50) {
      return false;
    }

    input.onChange(value);

    if (onValueChange) {
      onValueChange(value, false, false);
    }

    return true;
  };

  const handleFocus = (value: string) => {
    const clearedValue = clearAnonymization(value);

    if (clearedValue !== value) {
      input.onChange(clearedValue);
      if (onValueChange) {
        onValueChange(clearedValue, false, false);
      }
    }
  };

  const handleBlur = async (value: string) => {
    const formattedValue = getCapitalized(value);
    if (formattedValue !== input.value) {
      input.onChange(formattedValue);
    }

    const isCheckoutFirstCoownerNameControl =
      input.name === FieldsEnum.CheckoutCoowners1Name;
    const isCheckoutSecondCoownerNameControl =
      input.name === FieldsEnum.CheckoutCoowners2Name;

    const trackedPerson =
      questionKey === FieldsEnum.OwnerName ||
      questionKey === FieldsEnum.CheckoutOwnerName
        ? 'owner'
        : questionKey === FieldsEnum.Coowners1Name ||
          isCheckoutFirstCoownerNameControl
        ? 'coowner1'
        : questionKey === FieldsEnum.Coowners2Name ||
          isCheckoutSecondCoownerNameControl
        ? 'coowner2'
        : questionKey === FieldsEnum.CheckoutInsurantName
        ? 'different'
        : null;

    if (!formattedValue || !trackedPerson) {
      return;
    }

    const shouldTrackThisPerson = shouldCheckConsentResetForTrackedPerson(
      trackedPerson,
      insurantSelection
    );

    if (!shouldTrackThisPerson) {
      return;
    }

    const personGroup = getConsentResetPersonGroup(insurantSelection);
    const shouldCheck = getConsentResetPersonFlag(
      shouldCheckIfConsentResetRequired,
      personGroup,
      'name'
    );

    if (shouldCheck) {
      await checkAndResetMarketingConsentIfRequired({
        quotationId,
        dispatch,
        shouldCheck,
        requestData: {
          [personGroup]: {
            name: formattedValue,
          },
        },
        fieldToResetPath: {
          type: 'person',
          personGroup,
          field: 'name',
        },
        formName,
        consentFieldKey:
          formName === FormsEnum.CheckoutForm
            ? FieldsEnum.InsuranceMarketingConsent
            : FieldsEnum.InsuranceMarketingEmailConsent,
      });
    }
  };

  return {
    handleChange,
    handleFocus,
    handleBlur,
  };
}
